import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, List, Grid, ListItem, Typography, ListItemText, Button } from '@material-ui/core';
import { useStyles, listTextStyles, customCardStyles } from './CreditTypeDetailsWidget.styled';
import { Card } from 'components/UI';
import i18n from 'i18n-js';
import Can from 'components/Can';
import { creditTypesActions } from 'state/modules/creditTypes';
import { formatAmount } from 'utils';
import { CreateCreditTypeModal } from 'components/Modals';

const CreditTypeDetailsWidget = ({ id }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const creditType = useSelector((state) => state.creditTypes.creditType);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchCreditType = useCallback(
    (id) => {
      dispatch(creditTypesActions.fetchCreditTypeById(id));
    },
    [dispatch]
  );

  const toggleModal = () => {
    setShowCreateModal((state) => !state);
  };

  useEffect(() => {
    fetchCreditType(id);
  }, [fetchCreditType, id]);

  /**
   * Parse credit type data
   * @param {object} creditType Object with credit type data
   */
  const getCreditTypeInfo = (creditType = {}) => [
    {
      label: `${i18n.t(`common.type`)}:`,
      value: `${creditType?.accounting_type?.name || '-'}`,
    },
    {
      label: `${i18n.t(`common.name`)}:`,
      value: `${creditType?.name || '-'}`,
    },
    {
      label: `${i18n.t(`common.default_price`)}:`,
      value: `$${formatAmount(creditType?.default_unit_price || 0)}`,
    },
    {
      label: `${i18n.t(`common.description`)}:`,
      value: creditType?.description || '-',
      classes: 'fullWidth',
    },
  ];

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [];
  /**
   * Open update promo code modal
   */

  return (
    <Card
      menuItems={items}
      menuPermissions={{
        action: 'update',
        subject: 'invoice_item_type',
        type: 'can',
      }}
      title={i18n.t('credit_types.info')}
      headerActionType="vertical"
      contentRootClasses={cardClasses}
    >
      <Box className={classes.cardContentPadding}>
        <Grid container direction="row" alignItems="center" justifyContent="space-between">
          <Grid>
            <Typography variant="h3">
              <Box className={classes.name}>ID# {id}</Box>
            </Typography>
          </Grid>
          <Grid className={`${classes.textRight}`}>
            <Can I="update" a="invoice_item_type">
              <Typography>
                <Button
                  onClick={toggleModal}
                  data-cy="update-info"
                  className={classes.promoCodeLink}
                  color="primary"
                >
                  {i18n.t('common.update_information')}
                </Button>
              </Typography>
            </Can>
          </Grid>
        </Grid>

        <List className={classes.promoCodeList}>
          {getCreditTypeInfo(creditType).map((val, index) => (
            <ListItem key={index} className={classes.promoCodeListItem}>
              <ListItemText
                classes={{
                  root: val.classes || listClasses.root,
                  primary: listClasses.primary,
                  secondary: listClasses.secondary,
                }}
                primary={val.label}
                secondary={
                  <Box
                    display="flex"
                    component="span"
                    flexDirection="column"
                    className={!val.classes ? classes.textRight : ''}
                  >
                    <Typography variant="body2" component="span" color="textSecondary">
                      {val.value}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
        <CreateCreditTypeModal id={id} open={showCreateModal} toggleModal={toggleModal} />
      </Box>
    </Card>
  );
};

export { CreditTypeDetailsWidget };
